import { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import Switch from "react-switch";
import { universalSelectStylesNoFlex } from "../SelectStyle";

import styles from "./HunterFilter.module.css";
import { SlotOption } from "./EquipmentSlot";
import {
    MonsterHunterGame,
    MonsterHunterSlot,
    HunterOutfitFilter,
} from "@mythic/coms/types";
import HSeparator from "../HSeparator";

export const perPageOptions = [
    { value: 25, label: "25 Per Page" },
    { value: 50, label: "50 Per Page" },
    { value: 75, label: "75 Per Page" },
    { value: 100, label: "100 Per Page" },
] as const;

interface Props {
    filters: HunterOutfitFilter;
    perPage: number;
    onChange: (key: keyof HunterOutfitFilter | "perPage", value: any) => void;
    onSubmit: () => void;
    onReset: () => void;
}

export default function HunterFilter({
    filters,
    perPage,
    onChange,
    onReset,
    onSubmit,
}: Props) {
    const [armorDb, setArmorDb] = useState<
        (SlotOption & {
            slotType: MonsterHunterSlot;
            game: MonsterHunterGame;
            dyeable: boolean;
        })[]
    >([]);

    const [weaponDb, setWeaponDb] = useState<
        (SlotOption & { game: MonsterHunterGame })[]
    >([]);

    const [tagDb, setTagDb] = useState<{ label: string; value: string }[]>([]);

    useEffect(() => {
        Promise.all([
            fetch("https://wilds.mhdb.io/en/armor").then((response) => {
                return response.json();
            }),
            fetch("https://wilds.mhdb.io/en/weapons").then((response) => {
                return response.json();
            }),
            fetch(
                `${import.meta.env.VITE_SERVER_URL}/api/hunterfashion/tags/get`,
            ).then((res) => res.json()),
        ]).then(([armorJson, weaponJson, tagJson]) => {
            const armorData = armorJson.map((o: any) => {
                return {
                    value: o.id,
                    slotType: o.kind,
                    game: "Wilds",
                    dyeable: true,
                    label: o.name,
                };
            });

            const testSet = new Set();
            const weaponData = weaponJson
                .map((o: any) => {
                    return {
                        value: o.id,
                        label: o.name,
                    };
                })
                .filter((d: any) => {
                    if (testSet.has(d.label)) return false;

                    testSet.add(d.label);
                    return true;
                });

            setArmorDb(armorData);
            setWeaponDb(weaponData);
            setTagDb(tagJson.tags);
        });
    }, []);

    const headOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "head"),
        [armorDb],
    );

    const chestOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "chest"),
        [armorDb],
    );

    const armsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "arms"),
        [armorDb],
    );

    const waistOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "waist"),
        [armorDb],
    );

    const legsOptions = useMemo(
        () => armorDb.filter((a) => a.slotType === "legs"),
        [armorDb],
    );

    const handleFilterChange = (
        key: keyof HunterOutfitFilter | "perPage",
        value: any,
    ) => {
        onChange(key, value);
    };

    return (
        <div className={styles.outfitFilter}>
            <Select
                styles={universalSelectStylesNoFlex}
                options={perPageOptions}
                menuPortalTarget={document.body}
                value={perPageOptions.find((p) => p.value === perPage) as any}
                onChange={(val: any) =>
                    handleFilterChange("perPage", val?.value)
                }
            />
            <HSeparator
                text="Interactions"
                justify="left"
                style={{ margin: "1rem 0" }}
            />
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    placeItems: "center",
                }}
            >
                <Switch
                    checked={filters.favorite ?? false}
                    onChange={() => {
                        handleFilterChange("favorite", !filters.favorite);
                    }}
                    height={15}
                    handleDiameter={15}
                    width={32}
                />
                <span
                    style={{ flex: 1, textAlign: "left", marginLeft: "0.5em" }}
                >
                    My Favorites
                </span>
            </div>
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    placeItems: "center",
                }}
            >
                <Switch
                    checked={filters.liked ?? false}
                    onChange={() => {
                        handleFilterChange("liked", !filters.liked);
                    }}
                    height={15}
                    handleDiameter={15}
                    width={32}
                />
                <span
                    style={{ flex: 1, textAlign: "left", marginLeft: "0.5em" }}
                >
                    My Likes
                </span>
            </div>
            <HSeparator
                text="Creator"
                justify="left"
                style={{ margin: "1rem 0" }}
            />
            <input
                type="text"
                placeholder="Name or ID"
                value={filters.creatorId ?? ""}
                style={{ fontSize: "1.5rem" }}
                onChange={(ev) =>
                    handleFilterChange("creatorId", ev.target.value)
                }
            />
            <HSeparator
                text="Equipment"
                justify="left"
                style={{ margin: "1rem 0" }}
            />
            <Select
                styles={universalSelectStylesNoFlex}
                options={weaponDb}
                menuPortalTarget={document.body}
                value={weaponDb.find((w) => w.value === filters.weapon) ?? null}
                onChange={(val: any) =>
                    handleFilterChange("weapon", val?.value)
                }
                placeholder="Any Weapon"
                isSearchable
                isClearable
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={headOptions}
                menuPortalTarget={document.body}
                value={
                    headOptions.find((w) => w.value === filters.head) ?? null
                }
                onChange={(val: any) => handleFilterChange("head", val?.value)}
                placeholder="Any Head"
                isSearchable
                isClearable
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={chestOptions}
                menuPortalTarget={document.body}
                value={
                    chestOptions.find((w) => w.value === filters.chest) ?? null
                }
                onChange={(val: any) => handleFilterChange("chest", val?.value)}
                placeholder="Any Chest"
                isSearchable
                isClearable
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={armsOptions}
                menuPortalTarget={document.body}
                value={
                    armsOptions.find((w) => w.value === filters.arms) ?? null
                }
                onChange={(val: any) => handleFilterChange("arms", val?.value)}
                placeholder="Any Arms"
                isSearchable
                isClearable
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={waistOptions}
                menuPortalTarget={document.body}
                value={
                    waistOptions.find((w) => w.value === filters.waist) ?? null
                }
                onChange={(val: any) => handleFilterChange("waist", val?.value)}
                placeholder="Any Waist"
                isSearchable
                isClearable
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={legsOptions}
                menuPortalTarget={document.body}
                value={
                    legsOptions.find((w) => w.value === filters.legs) ?? null
                }
                onChange={(val: any) => handleFilterChange("legs", val?.value)}
                placeholder="Any Legs"
                isSearchable
                isClearable
            />

            <HSeparator
                text="Tags"
                justify="left"
                style={{ margin: "1rem 0" }}
            />

            <Select
                styles={universalSelectStylesNoFlex}
                options={tagDb}
                menuPortalTarget={document.body}
                value={tagDb.filter((t) => filters.tags?.includes(t.value))}
                onChange={(val: any, action) =>
                    handleFilterChange(
                        "tags",
                        val.map((v: any) => v.value),
                    )
                }
                placeholder="Any Tags"
                isSearchable
                isClearable
                isMulti
            />
            <div style={{ flexGrow: 1 }}></div>
            <button onClick={() => onSubmit()} style={{ margin: "0.25em 0" }}>
                Apply Filters
            </button>
            <button
                className={"border-button"}
                onClick={() => {
                    // setFilters({});
                    // onSubmit({});
                    onReset();
                }}
                style={{ margin: "0.25em 0" }}
            >
                Reset Filters
            </button>
        </div>
    );
}
